import { useState, useEffect } from 'react'
import { fetchSessionMeta, VTTSessionMeta } from '@/services/sessionMetaService'

interface UseSessionMetaOptions {
  sessionId: string
  token: string
  enabled?: boolean
}

interface UseSessionMetaResult {
  meta: VTTSessionMeta | null
  loading: boolean
  error: string | null
}

/**
 * Hook for fetching the v5 VTT session metadata (game title +
 * system, sessionNumber, scheduledTime, GM name, expected players).
 *
 * Same option shape as useCharacterData so the two can run in
 * parallel from App.tsx. Cached at the service layer; the hook
 * only owns its own React state.
 */
export function useSessionMeta({
  sessionId,
  token,
  enabled = true,
}: UseSessionMetaOptions): UseSessionMetaResult {
  const [meta, setMeta] = useState<VTTSessionMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!sessionId || !token || !enabled) return

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchSessionMeta(sessionId, token)
      .then((data) => {
        if (cancelled) return
        setMeta(data)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to fetch session metadata')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [sessionId, token, enabled])

  return { meta, loading, error }
}
